import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Layout } from '@/components/Layout';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState } from '@/components/EmptyState';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Toggle } from '@/components/ui/toggle';
import { BudgetLegend, CategoryRow } from '@/components/categories';
import { useCategories, useCreateCategory } from '@/hooks/useCategories';
import { Plus, Tags, Loader2, TrendingUp, TrendingDown, X, Check, CheckCheck } from 'lucide-react';
import {
  BUDGET_TYPES,
  CATEGORY_GROUP_PARAM,
  CATEGORY_PENDING_FILTER,
  transactionsOfCategory,
  type BudgetType,
} from '@/lib/constants';
import {
  BUDGET_ALL_CLASSIFIED,
  BUDGET_GROUP_EMPTY,
  BUDGET_GROUP_FILTER_CLEAR,
  BUDGET_PENDING_DONE,
  BUDGET_PENDING_DONE_ACTION,
  BUDGET_UNASSIGNED_LABEL,
  budgetGroupFilterLabel,
  budgetPendingProgress,
  translateBudgetType,
} from '@/lib/translations';
import { cn } from '@/lib/utils';
import { assignmentState } from '@/lib/utils/budget';
import { z } from 'zod';

const categorySchema = z.object({
  name: z.string().min(1, 'Nome da categoria é obrigatório').max(50, 'Nome muito longo'),
  type: z.enum(['income', 'expense'], { required_error: 'Selecione um tipo' }),
});

const EMPTY_STATES = {
  all: {
    icon: Tags,
    title: 'Nenhuma categoria ainda',
    description: 'Crie categorias para organizar suas transações.',
  },
  income: {
    icon: TrendingUp,
    title: 'Nenhuma categoria de receita',
    description: 'Crie categorias de receita para acompanhar seus ganhos.',
  },
  expense: {
    icon: TrendingDown,
    title: 'Nenhuma categoria de despesa',
    description: 'Crie categorias de despesa para acompanhar seus gastos.',
  },
} as const;

const isBudgetType = (value: string | null): value is BudgetType =>
  value !== null && (BUDGET_TYPES as readonly string[]).includes(value);

const SectionHeader = ({ type, count }: { type: 'income' | 'expense'; count: number }) => (
  <div className="flex items-center gap-2 mb-3">
    {type === 'income' ? (
      <TrendingUp className="h-4 w-4 text-income" />
    ) : (
      <TrendingDown className="h-4 w-4 text-expense" />
    )}
    <h3 className="text-sm font-semibold text-foreground">
      {type === 'income' ? 'Receitas' : 'Despesas'}
    </h3>
    <span className="text-xs font-normal text-muted-foreground ml-auto">
      {count} {count === 1 ? 'categoria' : 'categorias'}
    </span>
  </div>
);

const Categories: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', type: '' as 'income' | 'expense' | '' });
  const [errors, setErrors] = useState<{ name?: string; type?: string }>({});
  const [activeTab, setActiveTab] = useState<'all' | 'income' | 'expense'>('all');
  const [searchParams, setSearchParams] = useSearchParams();
  // Arrived from an Orçamento row: only the categories that feed that group, each with its way into Transações
  // for the month the row read. It takes precedence over the pending filter — a different question.
  const groupParam = searchParams.get(CATEGORY_GROUP_PARAM);
  const group = isBudgetType(groupParam) ? groupParam : null;
  const groupPeriod = searchParams.get('period') ?? undefined;
  const onlyPending =
    !group && searchParams.get(CATEGORY_PENDING_FILTER.param) === CATEGORY_PENDING_FILTER.value;
  // Rows classified while the filter is on stay in the list until it is switched off: they would otherwise
  // leave the moment their save lands, shifting the list under the pointer and taking the undo away.
  const [touched, setTouched] = useState<ReadonlySet<string>>(new Set());
  // Read out to screen readers when a save lands — a row's change is otherwise only visible.
  const [announcement, setAnnouncement] = useState('');

  // One unfiltered query — the cache entry the other pages already share. The tabs filter it client-side,
  // so the tab counts and the unassigned count always describe every category, not just the visible ones.
  const { data: categories = [], isLoading } = useCategories();
  const createCategory = useCreateCategory();

  const handleCreateCategory = () => {
    setErrors({});
    const validation = categorySchema.safeParse(newCategory);
    if (!validation.success) {
      const fieldErrors: { name?: string; type?: string } = {};
      validation.error.errors.forEach((err) => {
        if (err.path[0] === 'name') fieldErrors.name = err.message;
        if (err.path[0] === 'type') fieldErrors.type = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    createCategory.mutate(
      { name: newCategory.name, type: newCategory.type as 'income' | 'expense' },
      {
        onSuccess: () => {
          setNewCategory({ name: '', type: '' });
          setShowForm(false);
        },
      }
    );
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setNewCategory({ name: '', type: '' });
    setErrors({});
  };

  const handleTogglePending = (on: boolean) => {
    setTouched(new Set());
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params);
        if (on) next.set(CATEGORY_PENDING_FILTER.param, CATEGORY_PENDING_FILTER.value);
        else next.delete(CATEGORY_PENDING_FILTER.param);
        return next;
      },
      { replace: true }
    );
  };

  // Back to the whole list: the group, its month and the pending filter all go.
  const handleClearGroup = () => {
    setTouched(new Set());
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params);
        next.delete(CATEGORY_GROUP_PARAM);
        next.delete('period');
        next.delete(CATEGORY_PENDING_FILTER.param);
        return next;
      },
      { replace: true }
    );
  };

  // Registered when a request is sent, not when it succeeds: the list re-renders the instant the response
  // seeds the cache, and a row that has already left it can no longer report back.
  const handleRowSubmit = (categoryId: string) => setTouched((prev) => new Set(prev).add(categoryId));

  const incomeCount = categories.filter((c) => c.type === 'income').length;
  const expenseCount = categories.filter((c) => c.type === 'expense').length;
  const inTab = activeTab === 'all' ? categories : categories.filter((c) => c.type === activeTab);
  // The count on the toggle is what pressing it shows: unclassified categories within the current tab.
  const pendingInTab = inTab.filter((c) => assignmentState(c) === 'unassigned').length;
  // A row reclassified out of the group stays until the view changes, like the pending queue's rows.
  const visible = group
    ? inTab.filter((c) => c.budget_type === group || touched.has(c.id))
    : onlyPending
    ? inTab.filter((c) => assignmentState(c) === 'unassigned' || touched.has(c.id))
    : inTab;
  // The queue keeps classified rows (for undo), so the count is what says how far along it is.
  const pendingDone = onlyPending ? visible.filter((c) => assignmentState(c) !== 'unassigned').length : 0;
  const queueComplete = onlyPending && visible.length > 0 && pendingDone === visible.length;
  const sections = (['income', 'expense'] as const)
    .map((type) => ({ type, items: visible.filter((c) => c.type === type) }))
    .filter(({ items }) => items.length > 0);

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <Loader2 className="h-7 w-7 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <PageHeader
          title="Categorias"
          eyebrow={
            categories.length === 0
              ? 'Nenhuma categoria cadastrada'
              : categories.length === 1
              ? '1 categoria cadastrada'
              : `${categories.length} categorias cadastradas`
          }
          action={
            !showForm && (
              <button
                onClick={() => setShowForm(true)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity"
              >
                <Plus className="h-3.5 w-3.5" />
                Nova Categoria
              </button>
            )
          }
        />

        {/* Inline creation form — replaces dialog */}
        {showForm && (
          <div className="bg-card border border-border rounded-lg overflow-hidden animate-slide-down">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-muted/30">
              <h3 className="font-semibold text-foreground text-sm">Nova Categoria</h3>
              <button
                onClick={handleCloseForm}
                className="text-muted-foreground hover:text-foreground p-1 rounded-md hover:bg-muted transition-all"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="p-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="cat-name" className="text-sm font-medium">Nome</Label>
                  <Input
                    id="cat-name"
                    placeholder="Ex: Alimentação"
                    value={newCategory.name}
                    onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                    className={errors.name ? 'border-destructive' : ''}
                    onKeyDown={(e) => e.key === 'Enter' && handleCreateCategory()}
                  />
                  {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
                </div>

                <div className="space-y-1.5">
                  <Label className="text-sm font-medium">Tipo</Label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setNewCategory({ ...newCategory, type: 'income' })}
                      className={cn(
                        'flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-medium transition-all border',
                        newCategory.type === 'income'
                          ? 'bg-income/15 text-income border-income/35'
                          : 'bg-muted/50 text-muted-foreground border-border hover:border-income/30 hover:text-income/70'
                      )}
                    >
                      <TrendingUp className="h-3.5 w-3.5" />
                      Receita
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewCategory({ ...newCategory, type: 'expense' })}
                      className={cn(
                        'flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-medium transition-all border',
                        newCategory.type === 'expense'
                          ? 'bg-expense/15 text-expense border-expense/35'
                          : 'bg-muted/50 text-muted-foreground border-border hover:border-expense/30 hover:text-expense/70'
                      )}
                    >
                      <TrendingDown className="h-3.5 w-3.5" />
                      Despesa
                    </button>
                  </div>
                  {errors.type && <p className="text-xs text-destructive">{errors.type}</p>}
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-5">
                <button
                  onClick={handleCloseForm}
                  className="px-4 py-2 rounded-md text-sm text-muted-foreground hover:bg-muted transition-all"
                >
                  Cancelar
                </button>
                {/* aria-disabled, not disabled, while the request flies: a disabled button drops keyboard focus. */}
                <button
                  onClick={() => !createCategory.isPending && handleCreateCategory()}
                  aria-disabled={createCategory.isPending}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity aria-disabled:cursor-not-allowed aria-disabled:opacity-60"
                >
                  {createCategory.isPending ? (
                    <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Criando...</>
                  ) : (
                    <><Check className="h-3.5 w-3.5" /> Criar Categoria</>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <TabsList className="h-10">
              <TabsTrigger value="all" className="text-sm px-3 sm:px-5">
                Todas
                <span className="ml-2 text-[11px] tabular-nums text-muted-foreground">
                  {categories.length}
                </span>
              </TabsTrigger>
              <TabsTrigger value="income" className="text-sm px-3 sm:px-5">
                Receitas
                <span className="ml-2 text-[11px] tabular-nums text-muted-foreground">
                  {incomeCount}
                </span>
              </TabsTrigger>
              <TabsTrigger value="expense" className="text-sm px-3 sm:px-5">
                Despesas
                <span className="ml-2 text-[11px] tabular-nums text-muted-foreground">
                  {expenseCount}
                </span>
              </TabsTrigger>
            </TabsList>

            {/* Kept while the filter is on even at zero, so it is always the way back to the full list. */}
            {!group && (pendingInTab > 0 || onlyPending) && (
              <Toggle
                variant="outline"
                pressed={onlyPending}
                onPressedChange={handleTogglePending}
                className="gap-2 text-muted-foreground hover:bg-muted hover:text-foreground data-[state=on]:bg-foreground/10 data-[state=on]:text-foreground"
              >
                {BUDGET_UNASSIGNED_LABEL}{' '}
                <span className="text-[11px] tabular-nums text-muted-foreground">{pendingInTab}</span>
              </Toggle>
            )}
          </div>

          {categories.length > 0 && (
            <BudgetLegend defaultOpen={categories.every((c) => assignmentState(c) === 'unassigned')} />
          )}

          {/* The group view's own strip: what the list is narrowed to, and the way back. Hairline, no card. */}
          {group && (
            <div className="mt-4 flex items-center justify-between gap-3 border-y border-border py-1 text-sm">
              <p className="font-semibold text-foreground">{budgetGroupFilterLabel(translateBudgetType(group))}</p>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearGroup}
                className="-mr-3 h-11 text-muted-foreground hover:bg-muted hover:text-foreground sm:h-9"
              >
                {BUDGET_GROUP_FILTER_CLEAR}
              </Button>
            </div>
          )}

          {onlyPending && visible.length > 0 && (
            <div className="mt-4 flex min-h-11 flex-wrap items-center justify-between gap-x-3 text-sm sm:min-h-9">
              <p className={queueComplete ? 'font-semibold text-foreground' : 'tabular-nums text-muted-foreground'}>
                {queueComplete ? (
                  <>
                    <CheckCheck className="mr-1.5 inline h-4 w-4 align-text-bottom" aria-hidden="true" />
                    {BUDGET_PENDING_DONE}
                  </>
                ) : (
                  budgetPendingProgress(pendingDone, visible.length)
                )}
              </p>
              {queueComplete && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleTogglePending(false)}
                  className="-mr-3 h-11 text-muted-foreground hover:bg-muted hover:text-foreground sm:h-9"
                >
                  {BUDGET_PENDING_DONE_ACTION}
                </Button>
              )}
            </div>
          )}

          <TabsContent value={activeTab} className="mt-5">
            {inTab.length === 0 ? (
              <Card>
                <CardContent className="pt-6">
                  <EmptyState
                    {...EMPTY_STATES[activeTab]}
                    action={{ label: 'Nova Categoria', onClick: () => setShowForm(true) }}
                  />
                </CardContent>
              </Card>
            ) : group && visible.length === 0 ? (
              <Card>
                <CardContent className="pt-6">
                  <EmptyState icon={Tags} {...BUDGET_GROUP_EMPTY} />
                </CardContent>
              </Card>
            ) : visible.length === 0 ? (
              // No action: EmptyState's button is a filled amber one and the page already has that; the toggle
              // above stays visible and is the way back.
              <Card>
                <CardContent className="pt-6">
                  <EmptyState icon={CheckCheck} {...BUDGET_ALL_CLASSIFIED} />
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="pt-6 pb-6">
                  {sections.map(({ type, items }, index) => (
                    <section key={type} className={index > 0 ? 'mt-6 pt-6 border-t border-border' : undefined}>
                      {activeTab === 'all' && <SectionHeader type={type} count={items.length} />}
                      <ul className="divide-y divide-border/60">
                        {items.map((c) => (
                          <CategoryRow
                            key={c.id}
                            category={c}
                            defaultExpanded={onlyPending}
                            onSubmit={handleRowSubmit}
                            onSaved={setAnnouncement}
                            transactionsHref={group ? transactionsOfCategory(c.id, groupPeriod) : undefined}
                          />
                        ))}
                      </ul>
                    </section>
                  ))}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        <p role="status" className="sr-only">
          {announcement}
        </p>
      </div>
    </Layout>
  );
};

export default Categories;
